import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Briefcase, Search, Activity, ShieldCheck, Plus, Pencil, Trash2, X, Check, Phone, 
  ChevronDown, ChevronUp, MapPin, ExternalLink, Mail, Calendar, PhoneCall, Loader2
} from 'lucide-react';

interface DummyRecruiter {
  id: string;
  name: string;
  company_name: string;
  phone: string;
  email: string;
  status: string;
  account_status: string;
  avatar_url: string;
}

// ── CANDIDATE PROFILE MODAL (REUSED FOR CONSISTENCY) ──
const CandidateProfileModal = ({ 
  applicant, 
  onClose 
}: { 
  applicant: any, 
  onClose: () => void 
}) => {
  const { candidate } = applicant;
  
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 lg:p-8">
      <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full max-w-4xl max-h-[90vh] rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="p-6 lg:p-8 border-b border-slate-100 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-slate-900 flex items-center justify-center text-white text-2xl font-black shrink-0">
              {candidate.name?.[0] || '?'}
            </div>
            <div>
              <h2 className="text-2xl font-black text-slate-900 tracking-tight">{candidate.name}</h2>
              <p className="text-slate-500 flex items-center gap-2 text-sm">
                <MapPin size={14} /> {candidate.city} • <Calendar size={14} /> {candidate.experience_years} Years Experience
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-all"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 lg:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-12 space-y-8">
              
              {/* MEDIA & CONTEXT */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">CV Media</h3>
                  {candidate.cv_video_url ? (
                    <div className="aspect-video bg-slate-950 rounded-3xl overflow-hidden shadow-lg border-2 border-slate-900">
                      <video src={candidate.cv_video_url} controls className="w-full h-full object-contain" />
                    </div>
                  ) : (
                    <div className="aspect-video bg-slate-50 border-2 border-dashed border-slate-200 rounded-3xl flex flex-col items-center justify-center text-slate-400 p-6 text-center">
                      <Users size={24} className="mb-2" />
                      <p className="text-xs font-bold">No Media CV</p>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">Application Info</h3>
                  <div className="bg-slate-50 rounded-3xl p-6 border border-slate-100 space-y-4">
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Applying For</p>
                      <p className="text-lg font-black text-slate-900 leading-tight">{applicant.job_role}</p>
                      <p className="text-sm text-slate-500">{applicant.campaign_title}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* DETAILS */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 flex items-center gap-2">
                    <Briefcase size={14} /> Experience
                  </h3>
                  <div className="space-y-3">
                    {candidate.experience_details?.map((exp: any, idx: number) => (
                      <div key={idx} className="bg-white border border-slate-100 p-4 rounded-2xl shadow-sm">
                        <p className="font-bold text-slate-900">{exp.role || exp.title}</p>
                        <p className="text-sm text-slate-500">{exp.company}</p>
                      </div>
                    )) || <p className="text-sm text-slate-500 italic">No details provided.</p>}
                  </div>
                </div>
                <div className="space-y-4">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 flex items-center gap-2">
                    <MapPin size={14} /> Education
                  </h3>
                  <div className="space-y-3">
                    {candidate.education_details?.map((edu: any, idx: number) => (
                      <div key={idx} className="bg-white border border-slate-100 p-4 rounded-2xl shadow-sm">
                        <p className="font-bold text-slate-900">{edu.degree || edu.qualification}</p>
                        <p className="text-sm text-slate-500">{edu.institution}</p>
                      </div>
                    )) || <p className="text-sm text-slate-500 italic">No details provided.</p>}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-6 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
           <a 
            href={`tel:${candidate.phone}`}
            className="flex-1 max-w-[300px] bg-slate-900 text-white px-8 py-4 rounded-2xl font-black text-center shadow-lg hover:shadow-slate-900/20 transition-all flex items-center justify-center gap-3"
          >
            <PhoneCall size={20} />
            CALL CANDIDATE
          </a>
          <button className="px-6 py-4 text-slate-500 font-bold hover:text-slate-900" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// ── RECRUITER FORM MODAL ──
const RecruiterModal = ({ 
  recruiter, 
  onClose, 
  onSave 
}: { 
  recruiter?: DummyRecruiter | null, 
  onClose: () => void, 
  onSave: (data: any) => void 
}) => {
  const [formData, setFormData] = useState({
    name: recruiter?.name || '',
    company_name: recruiter?.company_name || '',
    phone: recruiter?.phone || '',
    email: recruiter?.email || '',
    account_status: recruiter?.account_status || 'active'
  });

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-950/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full max-w-lg rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-xl font-black text-slate-900">{recruiter ? 'Edit Recruiter' : 'Add New Recruiter'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Full Name</label>
            <input 
              type="text" 
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium focus:outline-none focus:border-slate-900 transition-all"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="e.g. John Doe"
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Company Name</label>
            <input 
              type="text" 
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium focus:outline-none focus:border-slate-900 transition-all"
              value={formData.company_name}
              onChange={(e) => setFormData({...formData, company_name: e.target.value})}
              placeholder="e.g. Acme Corp"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Phone Number</label>
              <input 
                type="tel" 
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium focus:outline-none focus:border-slate-900 transition-all"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="91XXXXXXXX"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Email Address</label>
              <input 
                type="email" 
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium focus:outline-none focus:border-slate-900 transition-all"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="john@example.com"
              />
            </div>
          </div>
          <div>
             <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Account Status</label>
             <select 
               className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium focus:outline-none focus:border-slate-900 transition-all capitalize"
               value={formData.account_status}
               onChange={(e) => setFormData({...formData, account_status: e.target.value})}
             >
               <option value="active">Active</option>
               <option value="suspended">Suspended</option>
               <option value="blocked">Blocked</option>
             </select>
          </div>
        </div>
        <div className="p-6 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-bold text-slate-500 hover:text-slate-900">Cancel</button>
          <button 
            onClick={() => onSave(formData)}
            className="bg-slate-900 text-white px-6 py-2 rounded-xl text-sm font-bold shadow-lg hover:shadow-slate-900/20 hover:-translate-y-0.5 transition-all flex items-center gap-2"
          >
            <Check size={18} />
            {recruiter ? 'Save Changes' : 'Create Recruiter'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── EXPANDABLE APPLICANTS SUB-COMPONENT ──
const RecruiterApplicants = ({ recruiterId, onSelect }: { recruiterId: string, onSelect: (app: any) => void }) => {
  const [apps, setApps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchApps = async () => {
      setLoading(true);
      try {
        const { data: campaigns } = await supabase.from('campaigns').select('id, title').eq('recruiter_id', recruiterId);
        if (!campaigns || campaigns.length === 0) { setApps([]); return; }
        const campaignIds = campaigns.map(c => c.id);

        const { data: jobs } = await supabase.from('jobs').select('id, role, campaign_id').in('campaign_id', campaignIds);
        if (!jobs || jobs.length === 0) { setApps([]); return; }
        const jobIds = jobs.map(j => j.id);

        const { data: applications } = await supabase.from('applications').select(`
            id, created_at, stage, job_id,
            candidate:candidates(*)
          `).in('job_id', jobIds).order('created_at', { ascending: false });

        if (applications) {
          const combined = applications.map(app => {
            const job = jobs.find(j => j.id === app.job_id);
            const campaign = campaigns.find(c => c.id === job?.campaign_id);
            return {
              ...app,
              job_role: job?.role || 'Unknown',
              campaign_title: campaign?.title || 'Unknown'
            };
          });
          setApps(combined);
        }
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchApps();
  }, [recruiterId]);

  if (loading) return <div className="py-4 px-10 flex items-center gap-3"><Loader2 className="animate-spin" size={16} /><p className="text-xs font-bold text-slate-400">Loading...</p></div>;
  if (apps.length === 0) return <div className="py-8 px-10 text-slate-400 italic text-sm">No applicants yet.</div>;

  return (
    <div className="bg-slate-50/50 border-t border-slate-100 px-10 py-6">
      <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-2">
        <Users size={12} className="text-blue-500" /> Recent Applicants ({apps.length})
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {apps.slice(0, 6).map((app) => (
          <div key={app.id} onClick={() => onSelect(app)} className="bg-white p-4 rounded-2xl border border-slate-200/60 shadow-sm cursor-pointer hover:border-slate-900 transition-all group/app">
            <div className="flex items-center justify-between mb-2">
              <p className="font-bold text-slate-900">{app.candidate?.name}</p>
              <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase bg-slate-100 text-slate-500">{app.stage}</span>
            </div>
            <p className="text-[11px] text-slate-500 flex items-center gap-1.5"><Briefcase size={10} /> {app.job_role}</p>
            <p className="text-[10px] text-slate-400 flex items-center gap-1.5 mt-1"><MapPin size={10} /> {app.candidate?.city}</p>
            <div className="mt-4 pt-3 border-t border-slate-50 flex items-center justify-between">
              <span className="text-[11px] font-black text-blue-600 flex items-center gap-1"><Phone size={10} /> {app.candidate?.phone}</span>
              <ExternalLink size={12} className="text-slate-300 group-hover/app:text-slate-900" />
            </div>
          </div>
        ))}
        {apps.length > 6 && <div className="flex items-center justify-center bg-white/40 border border-dashed border-slate-200 rounded-2xl p-4"><p className="text-[11px] font-bold text-slate-400">+ {apps.length - 6} more</p></div>}
      </div>
    </div>
  );
};

export default function MasterPanel() {
  const { user, setImpersonation } = useAuth();
  const navigate = useNavigate();
  const [dummies, setDummies] = useState<DummyRecruiter[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecruiter, setEditingRecruiter] = useState<DummyRecruiter | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedApplicant, setSelectedApplicant] = useState<any | null>(null);

  useEffect(() => { if (user) fetchDummies(); }, [user]);

  const fetchDummies = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('recruiters').select('*').eq('managed_by', user?.id).order('created_at', { ascending: false });
    if (!error && data) setDummies(data);
    setLoading(false);
  };

  const handleCreateOrUpdate = async (data: any) => {
    try {
      if (editingRecruiter) {
        await supabase.from('recruiters').update(data).eq('id', editingRecruiter.id);
      } else {
        await supabase.from('recruiters').insert({ ...data, id: crypto.randomUUID(), managed_by: user!.id, category: 'dummy', status: 'Verified', is_verified: true, verification_status: 'verified' });
      }
      setIsModalOpen(false); setEditingRecruiter(null); fetchDummies();
    } catch (err) { alert('Failed to save recruiter details.'); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure?')) return;
    await supabase.from('recruiters').delete().eq('id', id);
    fetchDummies();
  };

  const filteredDummies = dummies.filter(d => 
    (d.name || '').toLowerCase().includes(search.toLowerCase()) || 
    (d.company_name || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      
      {isModalOpen && <RecruiterModal recruiter={editingRecruiter} onClose={() => { setIsModalOpen(false); setEditingRecruiter(null); }} onSave={handleCreateOrUpdate} />}
      {selectedApplicant && <CandidateProfileModal applicant={selectedApplicant} onClose={() => setSelectedApplicant(null)} />}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900 flex items-center gap-2"><ShieldCheck className="text-yellow-500" /> Master Panel</h1>
          <p className="text-sm text-slate-500 mt-1">Manage managed recruiters and applicants.</p>
        </div>
        <button onClick={() => setIsModalOpen(true)} className="bg-slate-900 text-white px-5 py-3 rounded-2xl text-sm font-bold shadow-lg flex items-center gap-2 hover:-translate-y-0.5 transition-all">
          <Plus size={18} /> Add Recruiter
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4">
          <div className="bg-blue-100 p-3 rounded-xl text-blue-600"><Users size={22} /></div>
          <div><p className="text-[10px] font-bold text-slate-500 uppercase">Managed</p><p className="text-2xl font-black">{dummies.length}</p></div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4">
          <div className="bg-green-100 p-3 rounded-xl text-green-600"><Activity size={22} /></div>
          <div><p className="text-[10px] font-bold text-slate-500 uppercase">Active</p><p className="text-2xl font-black">{dummies.filter(d => d.account_status === 'active').length}</p></div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 p-4">
        <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 w-full max-w-md">
          <Search size={18} className="text-slate-400" />
          <input type="text" placeholder="Search recruiters..." className="bg-transparent border-none outline-none text-sm w-full font-medium" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-xl border border-slate-200/60 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm whitespace-nowrap">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="px-6 py-5">Recruiter Details</th>
                <th className="px-6 py-5">Contact</th>
                <th className="px-6 py-5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading ? (
                <tr><td colSpan={3} className="px-6 py-12 text-center text-slate-400">Loading fleet...</td></tr>
              ) : filteredDummies.map((recruiter) => {
                const isExpanded = expandedId === recruiter.id;
                return (
                  <React.Fragment key={recruiter.id}>
                    <tr className={`${isExpanded ? 'bg-slate-50' : 'hover:bg-slate-50/50'} transition-all group`}>
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-4">
                          <button onClick={() => setExpandedId(isExpanded ? null : recruiter.id)} className={`p-1.5 rounded-lg transition-all ${isExpanded ? 'bg-slate-900 text-white' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-900'}`}>{isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</button>
                          <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center font-black text-white">{recruiter.name?.[0]}</div>
                          <div><p className="font-bold text-slate-900">{recruiter.name}</p><p className="text-xs text-slate-400">{recruiter.company_name}</p></div>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <a href={`tel:${recruiter.phone}`} className="font-bold text-slate-800 hover:text-blue-600 flex items-center gap-2"><Phone size={14} className="text-slate-400" />{recruiter.phone}</a>
                        <p className="text-xs text-slate-400 lowercase">{recruiter.email}</p>
                      </td>
                      <td className="px-6 py-5 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => { setEditingRecruiter(recruiter); setIsModalOpen(true); }} className="p-2 text-slate-400 hover:text-blue-600"><Pencil size={18} /></button>
                          <button onClick={() => handleDelete(recruiter.id)} className="p-2 text-slate-400 hover:text-red-600"><Trash2 size={18} /></button>
                          <button onClick={() => { setImpersonation(recruiter.id, recruiter.name, recruiter.phone); navigate('/app/dashboard'); }} className="px-4 py-2 bg-slate-900 text-white text-xs font-black rounded-lg shadow-md active:scale-95 transition-all">IMPERSONATE</button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && <tr><td colSpan={3} className="p-0 border-b border-slate-100"><RecruiterApplicants recruiterId={recruiter.id} onSelect={setSelectedApplicant} /></td></tr>}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
